import React, { useContext, useEffect, useState, useRef } from "react";
import DataContext from "../contexts/DataProvider";
import swinkpay_logo from "../assets/swinklogo.png";
import pci from "../assets/pci.png";
import dateFormat from "dateformat";
import { isAndroid, isIOS } from "react-device-detect";
import FormatAmount from "../helper/FormatAmount";
import PaymentHeader from "../components/PaymentHeader";
import { FaLock } from "react-icons/fa";
import Page404 from "./Page404";
import Layout from "./Layout";
import analytics from "../helper/analytics";

/* Floating background particles */
const PARTICLES = [
  { size: 10, top: "8%",  left: "6%",  delay: "0s",    dur: "6s",   color: 0 },
  { size: 7,  top: "16%", left: "85%", delay: "1s",    dur: "8s",   color: 1 },
  { size: 12, top: "60%", left: "3%",  delay: "2s",    dur: "7s",   color: 0 },
  { size: 8,  top: "72%", left: "91%", delay: "0.5s",  dur: "9s",   color: 1 },
  { size: 10, top: "38%", left: "94%", delay: "1.5s",  dur: "6.5s", color: 2 },
  { size: 6,  top: "52%", left: "1%",  delay: "2.5s",  dur: "7.5s", color: 1 },
  { size: 11, top: "25%", left: "12%", delay: "3s",    dur: "8.5s", color: 2 },
  { size: 8,  top: "82%", left: "55%", delay: "0.8s",  dur: "7s",   color: 0 },
  { size: 7,  top: "45%", left: "8%",  delay: "1.8s",  dur: "9s",   color: 1 },
  { size: 9,  top: "88%", left: "20%", delay: "0.3s",  dur: "6s",   color: 2 },
  { size: 6,  top: "33%", left: "97%", delay: "2.2s",  dur: "8s",   color: 0 },
  { size: 10, top: "78%", left: "78%", delay: "1.2s",  dur: "7.5s", color: 1 },
];

/* Confetti dots shown on success — spread across full viewport */
const CONFETTI = [
  /* top band */
  { size: 7,  wide: false, top: "4%",  left: "10%", delay: "0.0s", dur: "2.4s", color: "#24CB7E" },
  { size: 5,  wide: true,  top: "6%",  left: "38%", delay: "0.3s", dur: "2.8s", color: "#1E56C8" },
  { size: 8,  wide: false, top: "3%",  left: "62%", delay: "0.1s", dur: "2.2s", color: "#F59E0B" },
  { size: 5,  wide: true,  top: "7%",  left: "82%", delay: "0.6s", dur: "2.6s", color: "#EC4899" },
  { size: 6,  wide: false, top: "5%",  left: "90%", delay: "0.9s", dur: "3.0s", color: "#A855F7" },
  { size: 4,  wide: true,  top: "2%",  left: "25%", delay: "1.2s", dur: "2.3s", color: "#F43F5E" },
  { size: 7,  wide: false, top: "8%",  left: "52%", delay: "0.4s", dur: "2.7s", color: "#06B6D4" },
  { size: 5,  wide: true,  top: "3%",  left: "72%", delay: "0.7s", dur: "2.5s", color: "#FBBF24" },
  /* left side */
  { size: 8,  wide: false, top: "22%", left: "2%",  delay: "0.2s", dur: "2.4s", color: "#24CB7E" },
  { size: 5,  wide: true,  top: "38%", left: "4%",  delay: "0.8s", dur: "2.9s", color: "#F59E0B" },
  { size: 6,  wide: false, top: "55%", left: "1%",  delay: "0.5s", dur: "2.6s", color: "#EC4899" },
  { size: 4,  wide: true,  top: "70%", left: "3%",  delay: "1.1s", dur: "2.2s", color: "#84CC16" },
  /* right side */
  { size: 7,  wide: false, top: "18%", left: "95%", delay: "0.3s", dur: "2.5s", color: "#1E56C8" },
  { size: 5,  wide: true,  top: "35%", left: "97%", delay: "0.9s", dur: "2.8s", color: "#A855F7" },
  { size: 8,  wide: false, top: "52%", left: "94%", delay: "0.6s", dur: "2.3s", color: "#F43F5E" },
  { size: 4,  wide: true,  top: "68%", left: "96%", delay: "1.3s", dur: "3.0s", color: "#06B6D4" },
  /* bottom band */
  { size: 6,  wide: false, top: "85%", left: "15%", delay: "0.4s", dur: "2.6s", color: "#FBBF24" },
  { size: 5,  wide: true,  top: "88%", left: "40%", delay: "0.2s", dur: "2.4s", color: "#24CB7E" },
  { size: 7,  wide: false, top: "82%", left: "65%", delay: "0.7s", dur: "2.7s", color: "#EC4899" },
  { size: 4,  wide: true,  top: "90%", left: "80%", delay: "1.0s", dur: "2.3s", color: "#1E56C8" },
  /* mid scatter */
  { size: 6,  wide: false, top: "30%", left: "8%",  delay: "1.5s", dur: "2.8s", color: "#A855F7" },
  { size: 5,  wide: true,  top: "48%", left: "91%", delay: "0.5s", dur: "2.5s", color: "#F59E0B" },
  { size: 7,  wide: false, top: "62%", left: "6%",  delay: "1.0s", dur: "2.2s", color: "#06B6D4" },
  { size: 4,  wide: true,  top: "75%", left: "93%", delay: "0.3s", dur: "3.1s", color: "#84CC16" },
];

/* Sparkle stars around success icon */
const SPARKLES = [
  { size: 7, angle: 0,   radius: 58, delay: "0.8s",  dur: "1.6s" },
  { size: 5, angle: 45,  radius: 56, delay: "1.0s",  dur: "1.4s" },
  { size: 6, angle: 90,  radius: 58, delay: "0.9s",  dur: "1.7s" },
  { size: 5, angle: 135, radius: 54, delay: "1.2s",  dur: "1.5s" },
  { size: 7, angle: 180, radius: 58, delay: "1.0s",  dur: "1.6s" },
  { size: 5, angle: 225, radius: 56, delay: "0.7s",  dur: "1.8s" },
  { size: 6, angle: 270, radius: 58, delay: "1.1s",  dur: "1.4s" },
  { size: 5, angle: 315, radius: 54, delay: "0.9s",  dur: "1.7s" },
];

// Floating ₹ coins for the "Almost there" interstitial motion graphics.
const FLOATING_COINS = [
  { left: "12%", size: 20, delay: "0s",   dur: "3.6s" },
  { left: "26%", size: 14, delay: "1.1s", dur: "4.2s" },
  { left: "40%", size: 24, delay: "0.5s", dur: "3.9s" },
  { left: "56%", size: 16, delay: "1.7s", dur: "4.5s" },
  { left: "70%", size: 22, delay: "0.3s", dur: "3.4s" },
  { left: "84%", size: 15, delay: "1.3s", dur: "4.1s" },
  { left: "48%", size: 12, delay: "2.2s", dur: "3.7s" },
  { left: "18%", size: 13, delay: "2.6s", dur: "4.4s" },
];

// Format an elapsed time (ms) as "X min Y sec" (or "Y sec" under a minute).
function formatTimeTaken(ms) {
  const total = Math.max(0, Math.round((ms || 0) / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m} min ${s} sec` : `${s} sec`;
}

// A merReturnURL that points back to our OWN /plugin page is not a real merchant
// redirect — following it just reloads the result screen, which loops forever (each
// reload re-arms the 4s auto-redirect) and re-fires the init APIs. Treat those as "no
// redirect" so we simply stay on the result screen.
function resolveMerchantReturnURL(url) {
  if (!url) return null;
  try {
    if (new URL(url, window.location.href).pathname.includes("/plugin")) return null;
  } catch (e) {
    /* malformed URL — fall through and use as-is */
  }
  return url;
}

// Toggle: when false, the "tap to reveal" interstitial is skipped and we rely on the plain
// history-trap (below) to keep Back on the status screen. Flip to true to bring the interstitial
// back if the plain trap isn't enough on real devices.
const INTERSTITIAL_ENABLED = false;

const SuccessOrFailed = ({ setIsNavigating }) => {
  const { data, setData } = useContext(DataContext);
  const [vw, setVw] = useState(window.innerWidth);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  // Rewards iframe is shown until the user skips OR the rewards page tells us the scratch/claim
  // is done (via postMessage). Once dismissed we fall back to the default success view.
  const [rewardDismissed, setRewardDismissed] = useState(false);
  const terminalNavRef = useRef(false);
  const terminalCapturedRef = useRef(false);

  // Interstitial gate for redirect-flow (netbanking/card) returns. Those land here via a
  // full-page gateway round-trip, which leaves the gateway in the browser back-history. We
  // gate the receipt behind ONE tap because that tap gives the page USER ACTIVATION — the
  // exact condition under which the browser KEEPS our history back-guard instead of discarding
  // it (a no-gesture guard is skipped, which is why Back was reaching the gateway). Arming the
  // guard inside the tap handler makes it gesture-backed (and sticky for the page lifetime), so
  // Back — and every re-trap — reliably stays on the plugin.
  const [revealed, setRevealed] = useState(!INTERSTITIAL_ENABLED);
  const revealedOnceRef = useRef(false);
  const reveal = () => {
    if (revealedOnceRef.current) return; // idempotent: screen + button can both fire in one tap
    revealedOnceRef.current = true;
    try {
      // Runs INSIDE the tap handler, so BOTH ops carry real user activation.
      // 1) replaceState rewrites the CURRENT history entry — this is the entry the browser
      //    flagged a cross-origin "redirect result" (the Cashfree -> plugin return). That flag
      //    is why Chrome SKIPS this entry on Back and lands on Cashfree. Rewriting it clears the
      //    flag and makes it a normal same-origin entry that Back will actually stop on.
      // 2) pushState adds a gesture-backed buffer on top. Because it now carries activation,
      //    Chrome will not discard it, so the first Back is a same-document pop that our
      //    PaymentMode popstate handler intercepts -> Transaction Guard modal. This is the whole
      //    reason the gate exists: the tap is the gesture that makes the guard survive.
      const here = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", here + "#/transaction-active");
      window.history.pushState(null, "", here + "#/transaction-active");
    } catch (e) {
      /* non-fatal */
    }
    setRevealed(true);
  };

  useEffect(() => {
    const handler = () => { setVw(window.innerWidth); setViewportHeight(window.innerHeight); };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // The rewards page runs in a cross-origin iframe, so we can't observe the scratch directly.
  // It signals completion (scratched / claimed / closed) via postMessage; when it does, we
  // dismiss the iframe and fall back to the default success view. Contract for the backend:
  //   window.parent.postMessage({ type: "reward_done" }, "*")
  // Any of the type values below are accepted. Origin is validated against the rewardsURL.
  useEffect(() => {
    const url = data?.rewardsURL;
    if (!url) return;
    let expectedOrigin = null;
    try { expectedOrigin = new URL(url).origin; } catch (e) { /* ignore malformed */ }
    const DONE_TYPES = [
      "reward_done", "reward_scratched", "reward_claimed", "reward_complete",
      "reward_close", "close_reward", "skip_reward", "rewards_done",
    ];
    const onMessage = (e) => {
      if (expectedOrigin && e.origin !== expectedOrigin) return;
      const t = typeof e.data === "string" ? e.data : e.data?.type;
      if (DONE_TYPES.includes(t)) setRewardDismissed(true);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [data?.rewardsURL]);

  // This screen renders ONLY at a terminal state, so capture completion here. This is
  // robust for card / netbanking / wallet, which reach it via a page reload where the
  // parent's status effect timing is unreliable. analytics.init() ensures the (possibly
  // resumed) session is started before we log; the guard prevents double-firing.
  useEffect(() => {
    const sc = data?.statusCode;
    if (![0, 1, 2].includes(sc)) return;
    if (terminalCapturedRef.current) return;
    terminalCapturedRef.current = true;
    const status = sc === 1 ? "success" : sc === 2 ? "failed" : "pending";
    analytics.init({ swinkpayId: data?.swinkpayID || undefined });
    analytics.track("transaction_completed", { statusCode: sc, status, swinkpayId: data?.swinkpayID || null });
    analytics.flush("transaction_completed");
  }, [data?.statusCode, data?.swinkpayID]);

  // Auto-redirect to the merchant after a few seconds — ONLY when merReturnURL is present.
  // If merReturnURL is null, stay on this screen (no redirect anywhere). The completion is
  // already captured on mount (effect above). Tapping Done fires the same redirect early.
  useEffect(() => {
    if (!revealed) return;                          // wait until the user taps through the gate
    const target = resolveMerchantReturnURL(data?.merReturnURL);
    if (!target) return;                            // null / self-referential → no redirect
    if (![1, 2].includes(data?.statusCode)) return; // only after a terminal result
    const t = setTimeout(() => {
      terminalNavRef.current = true;
      if (setIsNavigating) setIsNavigating(true);
      window.location.replace(target);
    }, 4000);
    return () => clearTimeout(t);
    // setIsNavigating excluded on purpose — it's an inline prop that changes every parent
    // render (Countdown ticks), which would otherwise reset the 4s timer so it never fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed, data?.merReturnURL, data?.statusCode]);

  // Safety timeout for the "View My Result" interstitial: if the user never taps, auto-reveal
  // the status after a few seconds so they ALWAYS see the final result (and the usual flow —
  // NOTE: the previous 8-second auto-reveal was intentionally REMOVED. Auto-revealing without a
  // tap left the page with NO user activation, so the back-guard was never armed and Back fell
  // through to Cashfree — the exact bug. The gate now has NO bypass: the user MUST tap to reveal
  // their result, and that tap is the gesture that arms the guard. (No result is hidden forever —
  // the result is one tap away and the button is the only, unmissable action on screen.)

  // Silent first-interaction arm (safety net for the removed interstitial).
  // With no interstitial there is no forced tap, so nothing gives the status page USER
  // ACTIVATION — and a no-gesture history buffer can be discarded by the browser, which is the
  // one case where an instant Back could still slip past the guard. The moment the user does
  // ANYTHING here (scroll, tap, key, wheel), we push one gesture-backed "#/transaction-active"
  // buffer. That single gesture makes activation STICKY for the page lifetime, so the back-guard
  // and every re-trap in PaymentMode are honored instead of skipped — no visible interstitial,
  // no extra tap. A user reading a receipt almost always scrolls/taps, so this arms in practice.
  useEffect(() => {
    if (![1, 2].includes(data?.statusCode)) return;
    if (typeof window === "undefined") return;
    let armed = false;
    // Include cursor/pointer MOVEMENT (mousemove/pointermove/mouseover), not just clicks —
    // on desktop the cursor moves almost immediately, so this arms the buffer without any
    // deliberate action. (Note: per the HTML spec plain move/scroll do not grant "user
    // activation" the way a click does, so this mainly refreshes the load-time buffer; the
    // guard still primarily rests on that load-time buffer, which the target browser honors.)
    const events = [
      "mousemove", "pointermove", "mouseover",
      "pointerdown", "touchstart", "keydown", "wheel", "scroll", "click",
    ];
    const opts = { capture: true, passive: true };
    const cleanup = () => events.forEach((ev) => window.removeEventListener(ev, arm, opts));
    function arm() {
      if (armed) return;
      armed = true;
      try {
        window.history.pushState(
          null,
          "",
          window.location.pathname + window.location.search + "#/transaction-active"
        );
      } catch (e) {
        /* non-fatal */
      }
      cleanup();
    }
    events.forEach((ev) => window.addEventListener(ev, arm, opts));
    return cleanup;
  }, [data?.statusCode]);

  if (data.show404) return <Page404 />;

  const isSuccess = data?.statusCode === 1; // 1 = success, 2 = failed, 0 = pending
  const isPending = data?.statusCode === 0;

  // Post-payment rewards page. When present (and the payment succeeded) it's embedded in the
  // hero area in place of the default checkmark graphic; otherwise the default hero is shown.
  // Once the user skips or the scratch completes (rewardDismissed), we revert to the default view.
  const rewardsURL = isSuccess ? (data?.rewardsURL || null) : null;
  const showReward = !!rewardsURL && !rewardDismissed;

  const firstSplit = data?.date?.split(" ") || [];
  const firstElement = firstSplit[0]?.split("-") || [];
  const secondElement = firstSplit[1]?.split(":") || [];
  let dateObj = "";
  if (firstElement.length > 0 && secondElement.length > 0) {
    dateObj = new Date(
      firstElement[0], parseInt(firstElement[1]) - 1, firstElement[2],
      secondElement[0], secondElement[1], secondElement[2]
    );
  } else {
    dateObj = new Date();
  }
  const date = dateFormat(dateObj, "dd mmm yyyy hh:MM TT").toString();

  // Total time taken to complete — from the activity session (tracks elapsed time from launch
  // and survives the gateway round-trip via sessionStorage). Shown on the receipt; hidden if
  // unavailable or implausibly short (< 2s, e.g. a direct receipt reload).
  let timeTaken = "";
  try {
    const summary = analytics.getSummary();
    if (summary && summary.sessionDurationMs >= 2000) {
      timeTaken = formatTimeTaken(summary.sessionDurationMs);
    }
  } catch (e) {
    /* ignore */
  }

  const accentColor  = isSuccess ? "#24CB7E" : isPending ? "#F59E0B" : "#DE554D";
  const accentLight  = isSuccess ? "rgba(36,203,126,0.12)" : isPending ? "rgba(245,158,11,0.10)" : "rgba(222,85,77,0.10)";
  const accentGrad   = isSuccess
    ? "linear-gradient(135deg, #24CB7E, #1aa36a)"
    : isPending 
    ? "linear-gradient(135deg, #F59E0B, #D97706)"
    : "linear-gradient(135deg, #DE554D, #c8403a)";
  const btnGrad      = isSuccess || isPending
    ? "linear-gradient(135deg, #1E56C8, #1340A0)"
    : "linear-gradient(135deg, #DE554D, #c8403a)";

  // ── "View your result" interstitial ──
  // Shown ONLY for redirect-flow (netbanking/card) returns — detected by the transactionId in
  // the URL the gateway returns to. UPI (in-page) and non-terminal states skip it. It is
  // deliberately STATUS-NEUTRAL (does NOT reveal success/failure) so the user is motivated to
  // tap to see their result — and that tap is what arms the back-guard (see reveal()). A safety
  // timeout (below) auto-reveals if the user never taps.
  const isRedirectReturn =
    typeof window !== "undefined" && window.location.search.indexOf("transactionId") !== -1;
  if (!revealed && isRedirectReturn && [1, 2].includes(data?.statusCode)) {
    const NEU = "#1E56C8";
    const NEU_GRAD = "linear-gradient(135deg, #1E56C8, #1340A0)";
    return (
      <div
        className="payment-page-bg"
        role="button"
        tabIndex={0}
        onClick={reveal}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); reveal(); } }}
        style={{ width:"100vw", height:isIOS||isAndroid?viewportHeight:"100vh", position:"relative", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", userSelect:"none" }}
      >
        {vw >= 640 && (<>
          <div className="absolute pointer-events-none" style={{ width:360, height:360, borderRadius:"50%", top:"-6%", left:"-6%", zIndex:0, filter:"blur(70px)", background:"radial-gradient(circle, rgba(30,86,200,0.22) 0%, transparent 70%)" }} />
          <div className="absolute pointer-events-none" style={{ width:300, height:300, borderRadius:"50%", bottom:"0%", right:"-4%", zIndex:0, filter:"blur(60px)", background:"radial-gradient(circle, rgba(168,85,247,0.16) 0%, transparent 70%)" }} />
        </>)}
        {PARTICLES.slice(0, 8).map((p, i) => (
          <div key={i} className="absolute rounded-full pointer-events-none" style={{ width:p.size, height:p.size, top:p.top, left:p.left, background: p.color===0?"rgba(30,86,200,0.35)":p.color===1?"rgba(36,203,126,0.30)":"rgba(168,85,247,0.30)", animation:`particle-float ${p.dur} ease-in-out infinite`, animationDelay:p.delay }} />
        ))}
        <div className={vw>=640?"card-glow":""} style={vw>=640?{ position:"relative", width:Math.min(Math.max(420, vw*0.42), vw-24), height:Math.min(720, window.innerHeight-24), borderRadius:16, overflow:"hidden", display:"flex", flexDirection:"column", zIndex:10, flexShrink:0 }:{ position:"relative", width:"100%", maxWidth:480, height:isIOS||isAndroid?viewportHeight:"100%", display:"flex", flexDirection:"column", zIndex:10 }}>
          {/* Header (shared white header) — neutral, does NOT reveal outcome */}
          <div className="flex-none w-full" style={{ height: 112 }}>
            <PaymentHeader
              amountLabel="AMOUNT"
              labelColor="#1E56C8"
              right={
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wide flex items-center gap-1.5 flex-shrink-0" style={{ background:"rgba(30,86,200,0.08)", color:"#1E56C8", border:"1px solid rgba(30,86,200,0.25)" }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background:"#1E56C8", animation:"ring-pulse 1.6s ease-out infinite" }} />FINALIZING
                </span>
              }
            />
          </div>

          {/* Body — neutral, builds anticipation */}
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center relative overflow-hidden" style={{ background:"#f5f8ff" }}>
            <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage:`radial-gradient(circle, ${NEU}1f 1.5px, transparent 1.5px)`, backgroundSize:"26px 26px", opacity:0.5 }} />

            {/* Motion graphics: ₹ coins floating up */}
            {FLOATING_COINS.map((c, i) => (
              <div key={i} className="sp-mini-coin"
                   style={{ left:c.left, bottom:"4%", width:c.size, height:c.size, fontSize:Math.round(c.size*0.55),
                            animation:`coin-float-up ${c.dur} ${c.delay} ease-in infinite` }}>₹</div>
            ))}

            <div className="relative flex items-center justify-center mb-6" style={{ width:130, height:130, perspective:700 }}>
              <div className="absolute rounded-full" style={{ width:118, height:118, background:"#F59E0B", opacity:0, animation:"ring-pulse 2.2s 0.2s ease-out infinite" }} />
              <div className="absolute rounded-full" style={{ width:118, height:118, background:"#F59E0B", opacity:0, animation:"ring-pulse 2.2s 1.0s ease-out infinite" }} />
              {/* rotating processing arc */}
              <div className="sp-proc-ring" />
              {/* sparkle glints orbiting the coin */}
              {SPARKLES.slice(0, 6).map((s, i) => {
                const x = Math.cos((s.angle * Math.PI) / 180) * s.radius;
                const y = Math.sin((s.angle * Math.PI) / 180) * s.radius;
                return (
                  <div key={i} className="absolute pointer-events-none"
                       style={{ width:s.size, height:s.size,
                         left:`calc(50% + ${x}px - ${s.size/2}px)`, top:`calc(50% + ${y}px - ${s.size/2}px)`,
                         background:"#FBBF24", borderRadius:"50%", zIndex:11,
                         animation:`sparkle-pop ${s.dur} ${s.delay} ease-in-out infinite` }} />
                );
              })}
              {/* Razorpay-style spinning ₹ coin */}
              <div className="sp-coin-wrap relative z-10">
                <div className="sp-coin">
                  <div className="sp-coin-face">₹</div>
                  <div className="sp-coin-face sp-coin-back">₹</div>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2" style={{ animation:"fade-up 0.4s 0.25s ease both" }}>Your receipt is ready</h2>
            <p className="text-sm text-gray-500 leading-relaxed mb-1" style={{ maxWidth:290, animation:"fade-up 0.4s 0.38s ease both" }}>
              One tap is needed to securely open your payment result and official receipt.
            </p>
            <p className="text-[13px] font-bold" style={{ color:NEU, animation:"fade-up 0.4s 0.5s ease both" }}>
              Tap anywhere on this screen to continue
            </p>
          </div>

          {/* Footer CTA */}
          <div className="flex-none bg-white border-t border-gray-100 px-4 pt-3 pb-3">
            <div className="flex items-center justify-center gap-1.5 mb-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background:NEU, animation:"ring-pulse 1.4s ease-out infinite" }} />
              <span className="text-[11px] text-gray-500 font-medium">Tap the button below (or anywhere on screen) to view your result</span>
            </div>
            <button type="button" onClick={(e) => { e.stopPropagation(); reveal(); }} className="relative w-full h-12 text-white text-[15px] font-bold rounded-xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl active:scale-[0.98] flex items-center justify-center gap-2" style={{ background:NEU_GRAD }}>
              <span>View My Result</span>
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" style={{ animation:"nudge-right 1.1s ease-in-out infinite" }}>
                <path d="M5 12h14M13 6l6 6-6 6" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="absolute inset-0 overflow-hidden pointer-events-none">
                <span style={{ position:"absolute", top:0, bottom:0, width:"40%", background:"linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)", animation:"shimmer-slide 2.2s ease-in-out infinite" }} />
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="payment-page-bg"
      style={{
        width: "100vw",
        height: isIOS || isAndroid ? viewportHeight : "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Large decorative blurred orbs — fills empty desktop space */}
      {vw >= 640 && (<>
        <div className="absolute pointer-events-none" style={{ width:380, height:380, borderRadius:"50%",
          top:"-8%", left:"-6%", zIndex:0, filter:"blur(70px)",
          background: isSuccess ? "radial-gradient(circle,rgba(36,203,126,0.22) 0%,transparent 70%)" : isPending ? "radial-gradient(circle,rgba(245,158,11,0.18) 0%,transparent 70%)" : "radial-gradient(circle,rgba(222,85,77,0.18) 0%,transparent 70%)" }} />
        <div className="absolute pointer-events-none" style={{ width:320, height:320, borderRadius:"50%",
          bottom:"2%", right:"-4%", zIndex:0, filter:"blur(60px)",
          background:"radial-gradient(circle,rgba(30,86,200,0.20) 0%,transparent 70%)" }} />
        <div className="absolute pointer-events-none" style={{ width:260, height:260, borderRadius:"50%",
          top:"30%", right:"4%", zIndex:0, filter:"blur(55px)",
          background: isSuccess ? "radial-gradient(circle,rgba(245,158,11,0.16) 0%,transparent 70%)" : "radial-gradient(circle,rgba(251,191,36,0.14) 0%,transparent 70%)" }} />
        <div className="absolute pointer-events-none" style={{ width:220, height:220, borderRadius:"50%",
          bottom:"18%", left:"2%", zIndex:0, filter:"blur(50px)",
          background:"radial-gradient(circle,rgba(168,85,247,0.14) 0%,transparent 70%)" }} />
        <div className="absolute pointer-events-none" style={{ width:180, height:180, borderRadius:"50%",
          top:"55%", left:"10%", zIndex:0, filter:"blur(45px)",
          background: isSuccess ? "radial-gradient(circle,rgba(6,182,212,0.14) 0%,transparent 70%)" : isPending ? "radial-gradient(circle,rgba(245,158,11,0.12) 0%,transparent 70%)" : "radial-gradient(circle,rgba(222,85,77,0.12) 0%,transparent 70%)" }} />
      </>)}

      {/* Background floating particles */}
      {PARTICLES.map((p, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: p.size, height: p.size,
            top: p.top, left: p.left,
            background: p.color === 0 ? `rgba(30,86,200,0.35)` : p.color === 1 ? `rgba(36,203,126,0.35)` : `rgba(168,85,247,0.30)`,
            animation: `particle-float ${p.dur} ease-in-out infinite`,
            animationDelay: p.delay,
          }}
        />
      ))}

      {/* Confetti on success */}
      {isSuccess && CONFETTI.map((c, i) => (
        <div
          key={i}
          className="absolute pointer-events-none"
          style={{
            width: c.wide ? c.size * 3 : c.size,
            height: c.size,
            top: c.top, left: c.left,
            background: c.color,
            borderRadius: c.wide ? "2px" : "50%",
            animation: `confetti-spiral ${c.dur} ${c.delay} ease-in infinite`,
          }}
        />
      ))}

      {/* Card container — constrained on desktop, scrollable centered column on mobile */}
      <div
        className={vw >= 640 ? "card-glow" : ""}
        style={vw >= 640 ? {
          position: "relative",
          width: Math.min(Math.max(420, vw * 0.42), vw - 24),
          height: Math.min(780, window.innerHeight - 24),
          borderRadius: 16, overflow: "hidden",
          display: "flex", flexDirection: "column", zIndex: 10, flexShrink: 0,
        } : {
          position: "relative",
          width: "100%", maxWidth: 480,
          height: isIOS || isAndroid ? viewportHeight : "100%",
          display: "flex", flexDirection: "column", zIndex: 10,
        }}
      >
        {/* ── Header (shared white header) — logo + "Powered by name"; amount + status below ── */}
        <div className="flex-none w-full" style={{ height: 112 }}>
          <PaymentHeader
            amountLabel={isSuccess ? "AMOUNT PAID" : "AMOUNT"}
            labelColor={accentColor}
            right={
              <span className="text-[11px] font-bold px-3 py-1 rounded-full tracking-wide flex-shrink-0"
                    style={{ background: accentLight, color: accentColor, border: `1px solid ${accentColor}55` }}>
                {isSuccess ? "✓ PAID" : isPending ? "⌛ PENDING" : "✕ FAILED"}
              </span>
            }
          />
        </div>

        {/* ── Body: hero (flex-1) + receipt (flex-none) — no empty space ── */}
        <div className="flex-1 flex flex-col overflow-hidden"
             style={{ background: isSuccess ? "#f0fdf4" : isPending ? "#fffbeb" : "#fff1f2" }}>

          {/* === HERO — rewards page when provided, else icon centered === */}
          {showReward ? (
            /* Reward unlocked — embed the merchant rewards page in the hero area. A compact
               success line stays on top (outcome still clear) with a Skip control; skipping OR
               a completion postMessage from the rewards page reverts to the default view. */
            <div className="flex-1 flex flex-col overflow-hidden px-3 pt-3 pb-1">
              <div className="flex-none flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center"
                       style={{ background: accentGrad, boxShadow: `0 4px 14px ${accentColor}55` }}>
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
                      <polyline points="4,13 9,18 20,7" stroke="white" strokeWidth="3"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h2 className="text-base font-extrabold text-gray-900 truncate">Payment Successful!</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setRewardDismissed(true)}
                  className="flex-shrink-0 flex items-center gap-0.5 text-[12px] font-semibold text-gray-500 pl-3 pr-2 py-1.5 rounded-full active:scale-95 transition-all"
                  style={{ background: "rgba(0,0,0,0.05)" }}
                >
                  Skip
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none">
                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 rounded-2xl overflow-hidden bg-white"
                   style={{ border: "1px solid rgba(0,0,0,0.08)", boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}>
                <iframe
                  src={rewardsURL}
                  title="Rewards"
                  className="w-full h-full"
                  style={{ border: 0, display: "block" }}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                />
              </div>
            </div>
          ) : (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-4 relative overflow-hidden">

            {/* Subtle dot grid backdrop */}
            <div className="absolute inset-0 pointer-events-none"
                 style={{ backgroundImage: `radial-gradient(circle, ${accentColor}28 1.5px, transparent 1.5px)`,
                          backgroundSize: "26px 26px", opacity: 0.6 }} />

            {/* Large icon with triple rings */}
            <div className="relative flex items-center justify-center mb-5"
                 style={{ width: 140, height: 140 }}>
              {/* Ring 1 */}
              <div className="absolute rounded-full" style={{ width: 124, height: 124,
                background: accentColor, opacity: 0, animation: "ring-pulse 2.4s 0.4s ease-out infinite" }} />
              {/* Ring 2 */}
              <div className="absolute rounded-full" style={{ width: 124, height: 124,
                background: accentColor, opacity: 0, animation: "ring-pulse 2.4s 1.1s ease-out infinite" }} />
              {/* Ring 3 */}
              <div className="absolute rounded-full" style={{ width: 124, height: 124,
                background: accentColor, opacity: 0, animation: "ring-pulse 2.4s 1.8s ease-out infinite" }} />

              {/* Sparkles on success */}
              {isSuccess && SPARKLES.map((s, i) => {
                const x = Math.cos((s.angle * Math.PI) / 180) * s.radius;
                const y = Math.sin((s.angle * Math.PI) / 180) * s.radius;
                return (
                  <div key={i} className="absolute pointer-events-none"
                       style={{ width: s.size, height: s.size,
                         left: `calc(50% + ${x}px - ${s.size/2}px)`,
                         top:  `calc(50% + ${y}px - ${s.size/2}px)`,
                         background: accentColor, borderRadius: "50%",
                         animation: `sparkle-pop ${s.dur} ${s.delay} ease-in-out infinite` }} />
                );
              })}

              {/* Icon circle */}
              <div className="w-24 h-24 rounded-full flex items-center justify-center relative z-10"
                   style={{ background: accentGrad,
                            boxShadow: `0 0 0 8px ${accentColor}18, 0 12px 40px ${accentColor}55` }}>
                {isSuccess ? (
                  <svg viewBox="0 0 24 24" className="w-12 h-12" fill="none">
                    <polyline points="4,13 9,18 20,7" stroke="white" strokeWidth="2.8"
                      strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : isPending ? (
                  <svg viewBox="0 0 24 24" className="w-11 h-11" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2.8" strokeLinecap="round" />
                    <polyline points="12,7 12,12 15,15" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="w-11 h-11" fill="none">
                    <line x1="6" y1="6" x2="18" y2="18" stroke="white" strokeWidth="2.8" strokeLinecap="round" />
                    <line x1="18" y1="6" x2="6" y2="18" stroke="white" strokeWidth="2.8" strokeLinecap="round" />
                  </svg>
                )}
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-extrabold text-gray-900 mb-1 text-center"
                style={{ animation: "fade-up 0.4s 0.3s ease both" }}>
              {isSuccess ? "Payment Successful!" : isPending ? "Payment Pending" : "Payment Failed"}
            </h2>

            {/* Subtitle */}
            <p className="text-sm text-gray-500 text-center leading-relaxed"
               style={{ maxWidth: 260, animation: "fade-up 0.4s 0.45s ease both" }}>
              {isSuccess
                ? "Transaction completed successfully"
                : isPending
                ? "Your transaction is still being processed."
                : "Your payment could not be processed. Please try again."}
            </p>

          </div>
          )}

          {/* === RECEIPT CARD — always at bottom of body === */}
          <div className="flex-none pb-3 px-4"
               style={{ animation: "card-slide-up 0.55s 0.2s cubic-bezier(0.34,1.56,0.64,1) both" }}>
            <div className="rounded-2xl overflow-hidden"
                 style={{ background: "white", maxWidth: 360, margin: "0 auto",
                          boxShadow: `0 -4px 24px rgba(0,0,0,0.07), 0 4px 12px rgba(0,0,0,0.06)`,
                          border: "1px solid rgba(0,0,0,0.07)" }}>

              {/* Accent top strip */}
              <div className="h-1" style={{ background: accentGrad }} />

              {/* Txn ID */}
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex-shrink-0">Txn ID</span>
                <div className="flex items-center gap-1.5 ml-3 min-w-0">
                  <p className="text-xs font-mono font-semibold text-gray-700 truncate" style={{ maxWidth: 160 }}>
                    {data?.swinkpayID}
                  </p>
                  <button type="button"
                          onClick={() => navigator.clipboard?.writeText(data?.swinkpayID || "")}
                          className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center active:scale-90 transition-transform"
                          style={{ background: "rgba(30,86,200,0.1)" }}>
                    <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none">
                      <rect x="9" y="9" width="13" height="13" rx="2" stroke="#1E56C8" strokeWidth="2"/>
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="#1E56C8" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="mx-4 h-px bg-gray-50" />

              {/* Date */}
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex-shrink-0">Date</span>
                <p className="text-xs font-semibold text-gray-700 ml-3">{date}</p>
              </div>

              {timeTaken && (<>
                <div className="mx-4 h-px bg-gray-50" />
                {/* Time taken */}
                <div className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex-shrink-0">Time Taken</span>
                  <p className="text-xs font-semibold text-gray-700 ml-3">{timeTaken}</p>
                </div>
              </>)}

              {/* Status footer strip */}
              <div className="flex items-center gap-2 px-4 py-2"
                   style={{ background: isSuccess ? "rgba(36,203,126,0.08)" : isPending ? "rgba(245,158,11,0.08)" : "rgba(222,85,77,0.08)",
                            borderTop: `1px solid ${accentColor}20` }}>
                <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                     style={{ background: accentColor + "22" }}>
                  <svg viewBox="0 0 24 24" className="w-2.5 h-2.5" fill="none">
                    {isSuccess
                      ? <polyline points="4,12 9,17 20,6" stroke={accentColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      : isPending
                      ? <circle cx="12" cy="12" r="10" stroke={accentColor} strokeWidth="2.5" />
                      : <path d="M12 9v4M12 17h.01" stroke={accentColor} strokeWidth="2.5" strokeLinecap="round" />
                    }
                  </svg>
                </div>
                <p className="text-[10px] font-semibold" style={{ color: accentColor }}>
                  {isSuccess ? "Secured & verified by SwinkPay" : isPending ? "Transaction is being processed" : "Transaction could not be completed"}
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="flex-none bg-white border-t border-gray-100">
          <div className="flex items-center justify-center gap-2 py-1.5 px-3">
            <FaLock className="text-[#1E56C8] text-xs" />
            <span className="text-[10px] text-gray-400 font-medium">256-bit Encrypted</span>
            <span className="text-gray-200 text-xs">|</span>
            <img src={pci} alt="pci_logo" className="h-5 w-auto" />
            <span className="text-gray-200 text-xs">|</span>
            <img src={swinkpay_logo} alt="swinkpay_logo" className="h-5 w-auto" />
          </div>
          <div className="flex items-center justify-center px-4 pb-3">
            <button
              type="button"
              onClick={() => {
                // Redirect to the merchant only when a real (non-self) merReturnURL is
                // provided; otherwise stay on the result screen (no redirect anywhere).
                const target = resolveMerchantReturnURL(data?.merReturnURL);
                if (target) {
                  terminalNavRef.current = true;
                  if (setIsNavigating) setIsNavigating(true);
                  window.location.replace(target);
                }
              }}
              className="w-full h-10 text-white text-sm font-semibold rounded-xl shadow-md transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
              style={{ background: btnGrad }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessOrFailed;
